import type { ChatMessage } from '@matter_extractor/shared';
import type { PendingChatMessage } from './chat-outbox';

/**
 * The order the thread is rendered in, and the two hours it shows
 * (chat spec §8.2).
 *
 * PURE. It is the module the whole lot turns on, and the reason is in the spec
 * rather than in the code: **the transcript renders in the order of ARRIVAL,
 * and the server's order wins.** Rendering a message at its place of WRITING
 * would show a conversation nobody lived, and the re-read - which is the
 * deliverable of the cycle - would draw conclusions about a sequence that never
 * happened.
 *
 * So `seq` sorts, always, and `composedAt` is only ever DISPLAYED - beside the
 * arrival, when the two diverge enough that the gap is itself the information.
 */

/**
 * One line of the thread.
 *
 * A union rather than a `pending: boolean` on one shape, because the two carry
 * different facts: a stored message has a `seq` and an arrival, a queued one has
 * neither and never will if it is refused. The union is what stops a renderer
 * reading a `seq` that is not there yet.
 */
export type ChatThreadRow =
  | { kind: 'sent'; message: ChatMessage }
  | { kind: 'pending'; message: PendingChatMessage };

/**
 * Folds new messages into what the client already holds.
 *
 * Two properties, and both are load-bearing:
 *
 *   - **deduplicated on `clientMessageId`**, not on `seq`. The same message
 *     arrives twice by design - once as the answer to its own POST, once as the
 *     socket delta - and after a reconnection the REST re-read hands over
 *     everything again. The key of §8.1 is the identity of a message across
 *     all three paths, which is exactly what it was minted for.
 *   - **sorted on `seq`**, ascending, always. Not on `receivedAt`, which is a
 *     stamp two writes could share, and never on `composedAt` - see the header.
 *
 * The incoming copy wins on a collision: it comes from the server, and the only
 * way the two differ is that one of them is older.
 */
export function mergeChatMessages(
  known: readonly ChatMessage[],
  incoming: readonly ChatMessage[],
): ChatMessage[] {
  const byKey = new Map<string, ChatMessage>();
  for (const message of known) byKey.set(message.clientMessageId, message);
  for (const message of incoming) byKey.set(message.clientMessageId, message);
  return [...byKey.values()].sort((left, right) => left.seq - right.seq);
}

/** The highest `seq` the client holds - the resynchronisation cursor (§7). */
export function highestChatSeq(messages: readonly ChatMessage[]): number {
  return messages.reduce((highest, message) => Math.max(highest, message.seq), 0);
}

/**
 * The thread as it is rendered: what arrived, then what has not left yet.
 *
 * The queued messages go LAST and never at their composition time among the
 * stored ones, which is the same rule as the header applied to a message that
 * has no arrival at all: it has not happened for the other person, so it cannot
 * be shown as though it had. They are also filtered against the stored set - a
 * message that has been acknowledged may still be in the queue for the length
 * of one render, and showing it twice would be the exact defect §8.1 exists to
 * prevent, moved into the DOM.
 */
export function chatThreadRows(
  messages: readonly ChatMessage[],
  pending: readonly PendingChatMessage[],
): ChatThreadRow[] {
  const stored = new Set(messages.map((message) => message.clientMessageId));
  return [
    ...messages.map((message): ChatThreadRow => ({ kind: 'sent', message })),
    ...pending
      .filter((message) => !stored.has(message.clientMessageId))
      .map((message): ChatThreadRow => ({ kind: 'pending', message })),
  ];
}

/**
 * How far apart the two stamps have to be before BOTH are shown.
 *
 * One minute, because the stamps are rendered to the minute (§13 of the issue:
 * « horodatages visibles, à la minute »): under a minute the two would print
 * the same string, and two identical times side by side is noise that teaches a
 * reader to stop looking at them. At a minute or more the gap is real, readable,
 * and - after a cut - the whole point.
 */
export const CHAT_STAMP_DIVERGENCE_MS = 60_000;

/**
 * Whether a stored message must show its composition hour beside its arrival.
 *
 * True exactly when the queue held it: a message that left immediately has two
 * stamps a second apart and one hour to show. A message written before a cut and
 * flushed after it carries both, which is what lets a re-reader see that the
 * words were written earlier than they landed - the case §8.2 is about, and the
 * one §12 asks the transcript to make visible.
 */
export function chatStampsDiverge(
  message: Pick<ChatMessage, 'composedAt' | 'receivedAt'>,
  thresholdMs: number = CHAT_STAMP_DIVERGENCE_MS,
): boolean {
  const composed = Date.parse(message.composedAt);
  const received = Date.parse(message.receivedAt);
  // An unreadable stamp shows one hour rather than a broken second one.
  if (Number.isNaN(composed) || Number.isNaN(received)) return false;
  return received - composed >= thresholdMs;
}

/**
 * Whether a message landed after the interview was closed (§11).
 *
 * DERIVED from two stamps that are already on the wire and never stored a third
 * time: the message's arrival against the room's closure. It is what makes a
 * late message « marked as having arrived after the closure » rather than
 * slipped in silently.
 */
export function chatArrivedAfterClosure(
  message: Pick<ChatMessage, 'receivedAt'>,
  closedAt: string | null,
): boolean {
  if (closedAt === null) return false;
  const received = Date.parse(message.receivedAt);
  const closed = Date.parse(closedAt);
  if (Number.isNaN(received) || Number.isNaN(closed)) return false;
  return received > closed;
}

/**
 * An hour, to the minute, in the reader's own timezone.
 *
 * To the minute and no finer: the rhythm is what the cycle wants to read - a
 * four-minute silence before an answer is itself matter - and seconds would
 * suggest a precision the exercise has no use for. In the reader's timezone
 * because both people are reading their own clock, and a transcript exported for
 * the administration carries the raw ISO stamps anyway.
 */
export function chatMinute(stamp: string): string {
  const at = new Date(stamp);
  if (Number.isNaN(at.getTime())) return '';
  const hours = String(at.getHours()).padStart(2, '0');
  const minutes = String(at.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}
